import { useDataProvider } from "react-admin";
import { Chip, Divider, Grid, IconButton, Typography } from "@material-ui/core";
import React, { useEffect, useState } from "react";
import { useField } from "react-final-form";
import { uniq } from "lodash";
import SanitizedGrid from "../../../components/SanitizedGrid";
import { GridinRowsWithSpace } from "utils/style";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "../../../components/layout/Menu";
import { changeSelectedRoute } from "../../../redux/navmode.action";
import Stack from "../../../components/Stack";
import KeyboardBackspaceIcon from "@material-ui/icons/KeyboardBackspace";
import {
  calculateDurationInString,
  getBusAssistantName,
  getBusAssistantsByIds,
  getDriverName,
  getDriversByIds,
  getSubcontractorName,
  getSubcontractorsByIds,
  getVehiclePlateNumber,
  getVehiclesByIds,
} from "../tools";
import CustomLabeled from "../../../components/layout/CustomLabeled";
import moment from "moment";
import CopyRouteInTextButton from "./CopyRouteInTextButton";
import ExportRouteInCsvButton from "./ExportRouteInCsvButton";
import RouteLocationListComponent from "./RouteLocationListComponent";

interface DetailRouteComponentProps {
  setIsOpenDetailRoute: any;
}

const DetailRouteComponent: React.FC<DetailRouteComponentProps> = ({
  setIsOpenDetailRoute,
}) => {
  const dispatch = useDispatch();
  const busScheduleField = useField("busSchedule");
  const schoolField = useField("school");

  const [subcontractors, setSubcontractors] = useState<any[]>([]);
  const [drivers, setDrivers] = useState<any[]>([]);
  const [busAssistants, setBusAssistants] = useState<any[]>([]);
  const [vehicles, setVehicles] = useState<any[]>([]);

  const [selectedData, setSelectedData] = React.useState<any>(null);

  const selectedRouteState = useSelector(
    (state: AppState) => state.navMode.route,
  );

  const provider = useDataProvider();

  const handleBackButton = () => {
    setIsOpenDetailRoute(false);
    dispatch(changeSelectedRoute(null));
  };

  useEffect(() => {
    if (selectedData === null) {
      setSelectedData({ ...selectedRouteState });
    }
  }, [selectedRouteState]);

  useEffect(() => {
    if (!selectedData) {
      return;
    }

    const driverIds = uniq([
      ...[selectedData.driverId],
      ...selectedData.backupDrivers,
    ]).filter((id: number) => id != null);

    const busAssistantIds = uniq([
      ...[selectedData.busAssistantId],
      ...selectedData.backupBusAssistants,
    ]).filter((id: number) => id != null);

    const vehicleIds = uniq([
      ...[selectedData.vehicleIds],
      ...selectedData.backupVehicles,
    ]).filter((id: number) => id != null);

    const subcontractorIds = [
      selectedData.assignedSubcontractor?.subcontractorId,
    ];

    if (subcontractorIds.length > 0) {
      getSubcontractorsByIds(provider, subcontractorIds).then((result) => {
        setSubcontractors(result);
      });
    }

    if (driverIds.length > 0) {
      getDriversByIds(provider, driverIds).then((result) => {
        setDrivers(result);
      });
    }

    if (busAssistantIds.length > 0) {
      getBusAssistantsByIds(provider, busAssistantIds).then((result) => {
        setBusAssistants(result);
      });
    }

    if (vehicleIds.length > 0) {
      getVehiclesByIds(provider, vehicleIds).then((result) => {
        setVehicles(result);
      });
    }
  }, [selectedData]);

  return (
    <>
      {selectedData ? (
        <SanitizedGrid container spacing={2}>
          <Grid item xs={12} style={GridinRowsWithSpace}>
            <div
              style={{
                display: "flex",
                flexDirection: "row",
              }}
            >
              <IconButton
                aria-label="Back"
                size="medium"
                onClick={handleBackButton}
                style={{ padding: "10px" }}
              >
                <KeyboardBackspaceIcon fontSize="inherit" />
              </IconButton>

              <Typography
                variant="h6"
                style={{ margin: "4px 0 0 4px" }}
                gutterBottom
              >
                {selectedData.routeName} Detail
              </Typography>
            </div>

            <div>
              <ExportRouteInCsvButton
                selectedData={selectedData}
                school={schoolField.input.value}
                busSchedule={busScheduleField.input.value}
              />
              <CopyRouteInTextButton
                selectedData={selectedData}
                school={schoolField.input.value}
                busSchedule={busScheduleField.input.value}
              />
            </div>
          </Grid>

          <Grid item xs={12}>
            <Divider style={{ margin: "10px 17px 0px 17px" }} />
          </Grid>

          <Grid item md={6} xs={12} style={{ padding: "24px" }}>
            <SanitizedGrid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Operation Detail
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <CustomLabeled name="Start Time">
                  <Typography>
                    {selectedData.startTime
                      ? moment(selectedData.startTime, "HH:mm").format("HH:mm")
                      : "-"}
                  </Typography>
                </CustomLabeled>
                <CustomLabeled name="End Time">
                  <Typography>
                    {selectedData.endTime
                      ? moment(selectedData.endTime, "HH:mm").format("HH:mm")
                      : "-"}
                  </Typography>
                </CustomLabeled>
                <CustomLabeled name="Duration">
                  <Typography>
                    {calculateDurationInString(
                      selectedData.startTime,
                      selectedData.endTime,
                    )}
                  </Typography>
                </CustomLabeled>
              </Grid>

              <Grid item xs={12}>
                <CustomLabeled name="Bus Bay Number">
                  <Typography>{selectedData.busBayNumber ?? "-"}</Typography>
                </CustomLabeled>
              </Grid>

              <Grid item xs={12}>
                <CustomLabeled name="Driver">
                  <Typography>
                    {selectedData.driverId
                      ? getDriverName(drivers, selectedData.driverId)
                      : "-"}
                  </Typography>
                </CustomLabeled>
                <CustomLabeled name="Backup Drivers">
                  {selectedData.backupDrivers.length > 0 ? (
                    selectedData.backupDrivers.map((driverId: number) => (
                      <Chip
                        size="small"
                        color="primary"
                        label={getDriverName(drivers, driverId)}
                      />
                    ))
                  ) : (
                    <Typography>-</Typography>
                  )}
                </CustomLabeled>
              </Grid>

              <Grid item xs={12}>
                <CustomLabeled name="Bus Assistant">
                  <Typography>
                    {selectedData.busAssistantId
                      ? getBusAssistantName(
                          busAssistants,
                          selectedData.busAssistantId,
                        )
                      : "-"}
                  </Typography>
                </CustomLabeled>
                <CustomLabeled name="Backup Bus Assistants">
                  {selectedData.backupBusAssistants.length > 0 ? (
                    selectedData.backupBusAssistantIds.map(
                      (busAssistantId: number) => (
                        <Chip
                          size="small"
                          color="primary"
                          label={getBusAssistantName(
                            busAssistants,
                            busAssistantId,
                          )}
                        />
                      ),
                    )
                  ) : (
                    <Typography>-</Typography>
                  )}
                </CustomLabeled>
              </Grid>

              <Grid item xs={12}>
                <CustomLabeled name="Vehicle">
                  <Typography>
                    {selectedData.vehicleId
                      ? getVehiclePlateNumber(vehicles, selectedData.vehicleId)
                      : "-"}
                  </Typography>
                </CustomLabeled>
                <CustomLabeled name="Backup Vehicles">
                  {selectedData.backupVehicles.length > 0 ? (
                    selectedData.backupVehicles.map((vehicleId: number) => (
                      <Chip
                        size="small"
                        color="primary"
                        label={getVehiclePlateNumber(vehicles, vehicleId)}
                      />
                    ))
                  ) : (
                    <Typography>-</Typography>
                  )}
                </CustomLabeled>
              </Grid>

              <Grid item xs={12}>
                <CustomLabeled name="Assigned Subcontractor">
                  <Typography>
                    {selectedData.assignedSubcontractor?.subcontractorId
                      ? getSubcontractorName(
                          subcontractors,
                          selectedData.assignedSubcontractor?.subcontractorId,
                        )
                      : "-"}
                  </Typography>
                </CustomLabeled>
                <CustomLabeled name="Vehicle Size">
                  <Typography>
                    {selectedData.assignedSubcontractor?.vehicleSize ?? "-"}
                  </Typography>
                </CustomLabeled>
                <CustomLabeled name="Start Date">
                  <Typography>
                    {selectedData.assignedSubcontractor?.startDate
                      ? moment(
                          selectedData.assignedSubcontractor?.startDate,
                        ).format("DD / MM / YYYY")
                      : "-"}
                  </Typography>
                </CustomLabeled>
                <CustomLabeled name="End Date">
                  <Typography>
                    {selectedData.assignedSubcontractor?.endDate
                      ? moment(
                          selectedData.assignedSubcontractor?.endDate,
                        ).format("DD / MM / YYYY")
                      : "-"}
                  </Typography>
                </CustomLabeled>
              </Grid>
            </SanitizedGrid>
          </Grid>
          <Grid item md={6} xs={12} style={{ padding: "24px" }}>
            <Grid item xs={12}>
              <Stack
                direction="row"
                justifyContent="space-between"
                style={{ marginBottom: "20px" }}
              >
                <Typography variant="h6" gutterBottom>
                  Locations and Students Detail
                </Typography>
              </Stack>

              <Divider />
            </Grid>

            <RouteLocationListComponent
              selectedData={selectedData}
              busSchedule={busScheduleField.input.value}
              school={schoolField.input.value}
            />
          </Grid>
        </SanitizedGrid>
      ) : (
        <></>
      )}
    </>
  );
};

export default DetailRouteComponent;
